import { CATALOG, needsOf } from "./catalog";
import { EVERY_SCREEN } from "../routes/registry";
import type { CatalogContext, Group, Setting } from "./types";

export const EMAILS_FAMILY_TITLE = "Emails";

/**
 * ═══ LES EMAILS DU SITE, RÉUNIS DANS « LE SITE » ═══
 *
 * Les emails d'action se règlent chacun sur l'écran qui les déclenche (connexion,
 * accompagnants, confirmation, badge). Pour savoir lesquels partent, il fallait donc
 * visiter quatre écrans. Cette famille les montre tous, rangés par écran.
 *
 * ⚠️ UN RÉCAPITULATIF, PAS UN DÉMÉNAGEMENT. Chaque email reste aussi sur son écran, et
 * cette famille n'est PAS déclarée dans `CATALOG` : elle y aurait doublé des clés que
 * `everySetting`, le récapitulatif et la carte d'un bloc rencontrent une seule fois. Le
 * panneau l'ajoute à l'onglet « Le site » au moment de l'afficher.
 *
 * ⚠️ LES CONDITIONS DE LA FAMILLE D'ORIGINE VOYAGENT AVEC L'EMAIL. L'email d'un
 * accompagnant ne sert à rien si l'étape des accompagnants n'est pas servie ; sur son
 * écran, c'est la famille entière qui disparaît. Ici, la condition de la famille
 * s'ajoute à celle du réglage, et la règle reste la même : ce qui ne gouverne rien ne
 * s'affiche pas.
 *
 * ⚠️ SANS `target`. Une ligne d'ici ne désigne pas un bloc de l'écran regardé : allumer
 * au survol le bouton de paiement depuis l'écran de connexion serait faux.
 */
export function emailsFamily(): Group {
  return {
    title: EMAILS_FAMILY_TITLE,
    help: "Les emails que le site envoie, écran par écran. Ils s'écrivent dans le back office ; ici, on voit lesquels partent et dans quelles langues. Chacun reste aussi réglable sur son écran.",
    settings: (context: CatalogContext) => collectEmails(context),
  };
}

export function collectEmails(context: CatalogContext): Setting[] {
  const out: Setting[] = [];
  const seen = new Set<string>();
  for (const [scope, groups] of Object.entries(CATALOG)) {
    const screen =
      scope === "site"
        ? "Le site"
        : (EVERY_SCREEN.find((s) => s.key === scope)?.label ?? scope);
    for (const group of groups) {
      const settings =
        typeof group.settings === "function"
          ? group.settings(context)
          : group.settings;
      const familyNeed =
        typeof group.needs === "function" ? group.needs(undefined) : group.needs;
      for (const setting of settings) {
        if (setting.type !== "action-email" || seen.has(setting.key)) continue;
        seen.add(setting.key);
        /* Ni cible, ni situation : ici, on regarde tous les emails à la fois, quelle
           que soit la réponse affichée sur l'écran de confirmation. */
        const {
          target: _target,
          needs: _needs,
          situations: _situations,
          ...rest
        } = setting;
        const needs = [...(familyNeed ? [familyNeed] : []), ...needsOf(setting)];
        out.push({
          ...rest,
          section: screen,
          ...(needs.length > 0 ? { needs } : {}),
        });
      }
    }
  }
  return out;
}
